import { CancelButton } from '@/components/formLayouts/CancelButton';
import { FormContainer, ValidationErrors } from '@/components/formLayouts/FormContainer';
import { SubmitButton } from '@/components/formLayouts/SubmitButton';
import { LayoutCard } from '@/components/LayoutCard';
import { PageLayoutCollapsibleSidebar } from '@/components/PageLayoutCollapsibleSidebar';
import { isTotalCostOfOwnershipEnabled } from '@/featureFlags';
import { useHttpMutation } from '@/resources/mutation/useHttpMutation';
import { UrlParams } from '@/utils/routes';
import { t } from '@lingui/macro';
import { Flexbox, Tab, Tabs, useBackNavigation } from '@luminovo/design-system';
import React from 'react';
import { NavigationSidebarSourcing } from '../Sourcing/components/NavigationSidebarSourcing';
import { GeneralTab } from './components/GeneralTab';
import { InventoryPreferencesTab } from './components/InventoryPreferencesTab';
import { SolutionPreferencesTab } from './components/SolutionPreferencesTab';
import { SolutionTagPreferncesTab } from './components/SolutionTagPreferncesTab';
import { SupplierPreferencesTab } from './components/SupplierPreferencesTab';
import { TotalCostOfOwnershipRulesTab } from './components/TotalCostOfOwnershipRulesTab';
import {
    convertSourcingScenarioFormValuesToPostDTO,
    SourcingScenarioFormValues,
    SourcingScenarioTab,
    useDefaultValuesSourcingScenario,
} from './components/types';

function ActionButton() {
    const { navigateBack } = useBackNavigation();
    return (
        <Flexbox gap={'8px'}>
            <CancelButton size={'medium'} onClick={() => navigateBack({ defaultPath: '/sourcing-templates' })} />
            <SubmitButton size={'medium'} />
        </Flexbox>
    );
}

function InnerContent({ rfqId }: { rfqId: string }) {
    const [tab, setTab] = React.useState<SourcingScenarioTab>('general');
    const { navigateBack } = useBackNavigation();
    const { mutateAsync } = useHttpMutation('POST /sourcing-scenarios', {
        snackbarMessage: t`Sourcing scenario created`,
        onSuccess: () => navigateBack({ defaultPath: '/' }),
    });

    const onSubmit = async (formValues: SourcingScenarioFormValues) => {
        await mutateAsync({
            requestBody: convertSourcingScenarioFormValuesToPostDTO(formValues),
        });
    };
    const defaultValues = useDefaultValuesSourcingScenario({ rfqId });

    const validationErrors: ValidationErrors<SourcingScenarioFormValues> = {
        'sourcing_scenario.missing_name': {
            fieldPath: 'general.name',
        },
        'sourcing_scenario.empty_assembly_quantities': {
            fieldPath: 'assemblyDemands',
        },
        'total_cost_of_ownership_rule.invalid_name': {
            fieldPath: 'root.serverError',
        },
        'total_cost_of_ownership_rule.conditions_cannot_be_empty': {
            fieldPath: 'root.serverError',
        },
        'total_cost_of_ownership_rule.absolute_cost_cannot_be_negative': {
            fieldPath: 'root.serverError',
        },
    };

    return (
        <FormContainer
            defaultValues={defaultValues}
            warnOnUnsavedChanges={true}
            validationErrors={validationErrors}
            onSubmit={onSubmit}
            UNSAFE_disableStablePropCheck
        >
            <LayoutCard title={t`Create sourcing scenario`} style={{ height: '100%' }} actions={<ActionButton />}>
                <span>
                    <Tabs size={'large'} value={tab} onChange={(_, value) => setTab(value)}>
                        <Tab value={'general'} label={t`General`} />
                        <Tab value={'solution-preference'} label={t`Solution preferences`} />
                        <Tab value={'solution-tags-preferences'} label={t`Solution tags preferences`} />
                        <Tab value={'supplier-preferences'} label={t`Supplier preferences`} />
                        <Tab value={'inventory-preferences'} label={t`Inventory preferences`} />
                        {isTotalCostOfOwnershipEnabled() && (
                            <Tab value={'total-cost-of-ownership-rules'} label={t`Total cost of ownership rules`} />
                        )}
                    </Tabs>
                </span>
                {tab === 'general' && <GeneralTab />}
                {tab === 'solution-preference' && <SolutionPreferencesTab />}
                {tab === 'solution-tags-preferences' && <SolutionTagPreferncesTab />}
                {tab === 'supplier-preferences' && <SupplierPreferencesTab />}
                {tab === 'inventory-preferences' && <InventoryPreferencesTab />}
                {tab === 'total-cost-of-ownership-rules' && <TotalCostOfOwnershipRulesTab />}
            </LayoutCard>
        </FormContainer>
    );
}

export function SourcingScenarioCreatePage({ pathParams }: UrlParams<'/rfqs/:rfqId/sourcing/scenarios/create'>) {
    const { rfqId } = pathParams;
    return (
        <PageLayoutCollapsibleSidebar sidebar={<NavigationSidebarSourcing rfqId={rfqId} />} layoutVariant="fullWidth">
            <InnerContent rfqId={rfqId} />
        </PageLayoutCollapsibleSidebar>
    );
}
